import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, Users, UserCheck, Package, Crown } from 'lucide-react';
import { getTierLimits, canAddCustomer, canAddKarigar, canAddOrder } from '../config/tiers';
import { useAuth } from '@/context/AuthContext';

interface TierRestrictionBannerProps {
  tierId: string;
  currentCounts: {
    customers: number;
    karigars: number;
    orders: number;
  };
  onUpgrade: () => void;
  type: 'customer' | 'karigar' | 'order';
}

const TierRestrictionBanner: React.FC<TierRestrictionBannerProps> = ({
  tierId,
  currentCounts,
  onUpgrade,
  type
}) => {
  const { user } = useAuth();
  const limits = getTierLimits(tierId);
  
  // Don't show banner for premium tailor test user
  if (user?.email === 'premium.tailor@masterji.com') {
    return null;
  }
  
  // Don't show karigar restriction banner for premium/enterprise tiers
  // Premium and enterprise accounts should not see upgrade prompts for karigars
  if (type === 'karigar' && (tierId === 'premium' || tierId === 'enterprise')) {
    return null;
  }
  
  const getRestrictionInfo = () => {
    switch (type) {
      case 'customer':
        return {
          icon: <Users className="w-5 h-5" />,
          current: currentCounts.customers,
          limit: limits.customers,
          canAdd: canAddCustomer(currentCounts.customers, tierId),
          message: `You have ${currentCounts.customers} of ${limits.customers === -1 ? 'unlimited' : limits.customers} customers`,
          actionText: 'Add Customer'
        };
      case 'karigar':
        return {
          icon: <UserCheck className="w-5 h-5" />,
          current: currentCounts.karigars,
          limit: limits.karigars,
          canAdd: canAddKarigar(currentCounts.karigars, tierId),
          message: `You have ${currentCounts.karigars} of ${limits.karigars === -1 ? 'unlimited' : limits.karigars} karigars`,
          actionText: 'Add Karigar'
        };
      case 'order':
        return {
          icon: <Package className="w-5 h-5" />,
          current: currentCounts.orders,
          limit: limits.orders,
          canAdd: canAddOrder(currentCounts.orders, tierId),
          message: `You have ${currentCounts.orders} of ${limits.orders === -1 ? 'unlimited' : limits.orders} orders`,
          actionText: 'Add Order'
        };
      default:
        return {
          icon: <AlertTriangle className="w-5 h-5" />,
          current: 0,
          limit: 0,
          canAdd: false,
          message: 'Limit reached',
          actionText: 'Upgrade'
        };
    }
  };

  const restrictionInfo = getRestrictionInfo();

  if (restrictionInfo.canAdd) {
    return null; // Don't show banner if within limits
  }

  const progressPercentage = restrictionInfo.limit === -1 
    ? 100 
    : Math.min((restrictionInfo.current / restrictionInfo.limit) * 100, 100);

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-lg p-4 mb-6"
    >
      <div className="flex items-start space-x-4">
        <div className="flex-shrink-0">
          <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
            {restrictionInfo.icon}
          </div>
        </div>
        
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            <h3 className="text-lg font-semibold text-orange-800">
              Plan Limit Reached
            </h3>
            <Crown className="w-4 h-4 text-orange-600" />
          </div>
          
          <p className="text-orange-700 mb-3">
            {restrictionInfo.message}. Upgrade your plan to add more {type}s.
          </p>
          
          {/* Progress Bar */}
          <div className="mb-4">
            <div className="flex justify-between text-sm text-orange-600 mb-1">
              <span>{restrictionInfo.current} used</span>
              <span>{restrictionInfo.limit === -1 ? 'unlimited' : restrictionInfo.limit} limit</span>
            </div>
            <div className="w-full bg-orange-200 rounded-full h-2">
              <div
                className="bg-orange-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={onUpgrade}
              className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors flex items-center space-x-2"
            >
              <Crown className="w-4 h-4" />
              <span>Upgrade Plan</span>
            </button>
            <span className="text-sm text-orange-600">
              Starting from $29/month
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default TierRestrictionBanner;
